import React from 'react';
import { PRODUCT_MOCKUP, PRODUCT_MOCKUP_ALT } from '../data/product';

/** Small stick-figure gymnast in a star shape (flat line style). */
function StarGymnast({
  x,
  y,
  s = 1,
  color = '#16213E'
}: {
  x: number;
  y: number;
  s?: number;
  color?: string;
}) {
  return <g transform={`translate(${x} ${y}) scale(${s})`} stroke={color} strokeWidth="3.2" strokeLinecap="round" fill="none" data-id="element-255" data-mp="3shx9e66vg514">
      <circle cx="0" cy="-26" r="6" fill={color} stroke="none" data-id="element-256" data-mp="o1mg4tru3gq2" />
      <path d="M0 -18 L0 4" data-id="element-257" data-mp="o3fme7yjbyxj" />
      <path d="M0 -14 L-18 -26 M0 -14 L18 -26" data-id="element-258" data-mp="t0604omvaifs" />
      <path d="M0 4 L-14 24 M0 4 L14 24" data-id="element-259" data-mp="szxpl3e7vp7r" />
    </g>;
}

/**
 * Shows the uploaded mockup when PRODUCT_MOCKUP is set; otherwise draws the
 * cover in code: Cloud page, apparatus colour strip, cartwheel trajectory.
 */
export function ProductVisual({
  className = '',
  lazy = false
}: {
  className?: string;
  lazy?: boolean;
}) {
  if (PRODUCT_MOCKUP) {
    return <img src={PRODUCT_MOCKUP} alt={PRODUCT_MOCKUP_ALT} loading={lazy ? 'lazy' : undefined} className={`mx-auto h-auto w-full rounded-3xl shadow-book ring-4 ring-white/10 ${className}`} data-id="element-260" data-mp="1brgmqtlk891v" />;
  }
  return <div className={`relative mx-auto ${className}`} role="img" aria-label={PRODUCT_MOCKUP_ALT} data-id="element-261" data-mp="1zf2upy1s1o7s">
      {/* the other volumes, fanned behind */}
      <div className="absolute inset-0 translate-x-[9%] translate-y-[2%] rotate-[6deg] rounded-[14px] bg-amber shadow-book" aria-hidden="true" data-id="element-262" data-mp="29axjzc0iahjj" />
      <div className="absolute inset-0 translate-x-[6%] translate-y-[1.5%] rotate-[4deg] rounded-[14px] bg-teal shadow-book" aria-hidden="true" data-id="element-263" data-mp="2n19dnfcqoocs" />
      <div className="absolute inset-0 translate-x-[3%] translate-y-[1%] rotate-[2deg] rounded-[14px] bg-indigo shadow-book" aria-hidden="true" data-id="element-264" data-mp="2x0rtpkpj3pzk" />

      <div style={{
      containerType: 'inline-size'
    }} className="relative aspect-[4/3] overflow-hidden rounded-[14px] bg-white shadow-book" data-id="element-265" data-mp="2s2gm7gmphrki">
        <span className="apparatus-strip absolute inset-x-0 top-0 h-[3.2%]" aria-hidden="true" data-id="element-266" data-mp="xofleq7liuhs" />
        <div className="relative px-[7%] pt-[8%]" data-id="element-267" data-mp="2xqowyia6j52u">
          <p className="font-display text-[3cqw] font-bold uppercase tracking-[0.2em] text-coral-deep" data-id="element-268" data-mp="2xs00qi3mkcty">
            A full year · 52 weekly classes
          </p>
          <p className="mt-[2cqw] font-display text-[8.6cqw] font-extrabold leading-[0.95] tracking-tight text-ink" data-id="element-269" data-mp="36psidswtjwki">
            Rec Gymnastics
            <br data-id="element-270" data-mp="3bnu1gdu8vvvz" />
            Class-Ready
          </p>
          <p className="mt-[2.4cqw] max-w-[62%] font-sans text-[3.1cqw] font-medium leading-snug text-slate" data-id="element-271" data-mp="3gnyjw9epr0ou">
            Done-for-You Lesson Plans &amp; Skill Progressions
          </p>
        </div>

        {/* floor lane + cartwheel trajectory */}
        <svg viewBox="0 0 400 300" className="absolute inset-0 h-full w-full" aria-hidden="true" data-id="element-272" data-mp="3qh70tliq9ahb">
          <rect x="0" y="238" width="400" height="62" fill="#F4F6FB" data-id="element-273" data-mp="3ui8za04tlii8" />
          <line x1="24" y1="258" x2="376" y2="258" stroke="#FF5A5F" strokeWidth="3" strokeDasharray="2 9" strokeLinecap="round" data-id="element-274" data-mp="3bofn1h3hcv8" />
          <path d="M196 250 C 240 150, 330 150, 362 250" stroke="#2541B2" strokeWidth="2.4" strokeDasharray="6 7" fill="none" data-id="element-275" data-mp="86varkwat98j" />
          <circle cx="262" cy="258" r="5" fill="#FF5A5F" data-id="element-276" data-mp="ddilzpo4i4e6" />
          <circle cx="290" cy="258" r="5" fill="#FF5A5F" data-id="element-277" data-mp="dg4ubriu8nxg" />
          <circle cx="318" cy="258" r="5" fill="#17B8A6" data-id="element-278" data-mp="8g0abriq1b2h" />
          <circle cx="346" cy="258" r="5" fill="#17B8A6" data-id="element-279" data-mp="4ddgxm6eji36" />
          <StarGymnast x={300} y={196} s={1.25} data-id="element-280" data-mp="4eyab8gzcuzg" />
          {/* level dots */}
          <g transform="translate(28 222)" data-id="element-281" data-mp="112l2gu70tr73">
            <circle cx="0" cy="0" r="5" fill="#17B8A6" data-id="element-282" data-mp="115h0sjms6hw2" />
            <circle cx="16" cy="0" r="5" fill="#FFC53D" data-id="element-283" data-mp="w5ums7z4oxm7" />
            <circle cx="32" cy="0" r="5" fill="#FF5A5F" data-id="element-284" data-mp="rdtbao9sbax8" />
          </g>
          <text x="28" y="282" fill="#5A6072" fontFamily="Montserrat, sans-serif" fontWeight="700" fontSize="11" letterSpacing="2" data-id="element-285" data-mp="rbqe9ka7shj0">
            FLOOR · BARS · BEAM · VAULT
          </text>
        </svg>
      </div>
    </div>;
}